import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useFeeConfig } from "@/lib/fees";
import { StatusPipeline } from "@/components/StatusPipeline";
import { ArrowLeft, Download, FileArrowDown, CreditCard, Coins, LinkSimple, ImageSquare, UploadSimple, CheckCircle, Star, Archive } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ProjectMessages } from "@/components/ProjectMessages";
import { PromoCodeField } from "@/components/PromoCodeField";
import { ExitSurvey } from "@/components/ExitSurvey";
import { ReviewSubmitModal } from "@/components/ReviewSubmitModal";
import { ShareProjectButton } from "@/components/ShareProjectButton";
import { LaunchImageModal } from "@/components/LaunchImageModal";

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { transaction_fee_pct: feePct } = useFeeConfig();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [promo, setPromo] = useState("");
  const [approving, setApproving] = useState(false);
  const [approvingQuote, setApprovingQuote] = useState(false);
  const [reviewModal, setReviewModal] = useState(false);
  const [launchOpen, setLaunchOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const startPayment = async (type) => {
    setPaying(true);
    try {
      const { data } = await api.post("/checkout/session", {
        type,
        reference_id: id,
        origin_url: window.location.origin,
        promo_code: promo || undefined,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setPaying(false);
    }
  };

  const approveQuote = async () => {
    if (!window.confirm("Approve this quote and lock the scope? You'll get a signed PDF for your records and can pay the deposit next.")) return;
    setApprovingQuote(true);
    try {
      await api.post(`/projects/${id}/approve-quote`);
      toast.success("Quote approved — signed PDF sent to your email.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not approve quote");
    } finally {
      setApprovingQuote(false);
    }
  };

  const downloadSignedQuote = async () => {
    try {
      const res = await api.get(`/projects/${id}/signed-quote.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `signed-quote-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download signed quote");
    }
  };

  const approveAndClose = async () => {
    if (!window.confirm("Approve this project and close it out? It will move to your archive — you'll still be able to view it anytime.")) return;
    setApproving(true);
    try {
      await api.post(`/projects/${id}/approve`);
      toast.success("Project approved & archived. Thanks for your business.");
      // Open review modal next
      setReviewModal(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not approve");
    } finally {
      setApproving(false);
    }
  };

  const downloadFile = async (deliverable) => {
    if (deliverable.kind === "link" && deliverable.url) {
      window.open(deliverable.url, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const resp = await api.get(`/deliverables/${deliverable.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: deliverable.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = deliverable.original_filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const downloadAllZip = async () => {
    try {
      const resp = await api.get(`/projects/${id}/download-all`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
      const a = document.createElement("a");
      const disposition = resp.headers["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(disposition);
      a.href = url;
      a.download = match?.[1] || "project-files.zip";
      a.click();
      window.URL.revokeObjectURL(url);
      const included = resp.headers["x-files-included"];
      if (included) toast.success(`Downloaded ${included} file${included === "1" ? "" : "s"} as ZIP`);
    } catch (e) {
      if (e?.response?.status === 404) toast.error("No files to download for this project");
      else toast.error("ZIP download failed");
    }
  };

  if (loading) return <ClientLayout><div className="text-[#a3a3a3]">Loading…</div></ClientLayout>;
  if (!project) return <ClientLayout><div>Project not found.</div></ClientLayout>;

  const balance = Math.max(0, (project.total_usd || 0) - (project.paid_amount || 0));
  const isPaidPoints = project.payment_method === "points";

  return (
    <ClientLayout>
      <div className="flex items-center justify-between mb-6 gap-3">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#a3a3a3] hover:text-white" data-testid="back-link">
          <ArrowLeft size={16} /> Back to projects
        </Link>
        <ShareProjectButton projectId={id} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
        <div className="lg:col-span-8">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">{project.service_type}</div>
          <h1 className="font-display text-5xl md:text-6xl mt-2" data-testid="project-title">{project.title}</h1>
          <div className="text-[#a3a3a3] mt-3 max-w-2xl">{project.description}</div>
        </div>
        <div className="lg:col-span-4 grid grid-cols-2 gap-px bg-[#262626] border border-[#262626]">
          <div className="bg-[#0a0a0a] p-4 min-w-0">
            <div className="text-[10px] text-[#a3a3a3] uppercase tracking-wider">Total</div>
            <div className="font-display text-lg sm:text-xl md:text-2xl mt-1 tabular-nums truncate">
              {isPaidPoints ? `${project.total_points} pts` : formatUSD(project.total_usd)}
            </div>
          </div>
          <div className="bg-[#0a0a0a] p-4 min-w-0">
            <div className="text-[10px] text-[#a3a3a3] uppercase tracking-wider">Paid</div>
            <div className="font-display text-lg sm:text-xl md:text-2xl mt-1 text-[#FF2D2D] tabular-nums truncate">
              {isPaidPoints ? "—" : formatUSD(project.paid_amount)}
            </div>
          </div>
          <div className="bg-[#0a0a0a] p-4 col-span-2">
            <div className="text-[10px] text-[#a3a3a3] uppercase tracking-wider">Status</div>
            <div className="font-display text-xl mt-1 capitalize">{project.status}</div>
          </div>
        </div>
      </div>

      <h2 className="font-display text-2xl mb-4 uppercase">Pipeline</h2>
      <StatusPipeline status={project.status} />

      {project.assigned_designer_name && (
        <div className="mt-6 border border-[#262626] bg-[#0a0a0a] p-4 flex items-center gap-3" data-testid="designer-info">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Your designer</span>
          <span className="font-display text-lg">{project.assigned_designer_name}</span>
        </div>
      )}

      {/* Awaiting-quote / quote-sent RFP panels */}
      {project.payment_status === "awaiting_quote" && (
        <div className="mt-10 border border-[#FFA94D] bg-[#FFA94D]/5 p-6" data-testid="awaiting-quote-panel">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFA94D]">Awaiting quote</div>
          <div className="font-display text-2xl mt-2">We're preparing your written quote.</div>
          <p className="text-sm text-[#a3a3a3] mt-3 max-w-2xl">
            Thanks for the brief — we'll email a formal quote shortly. No card is charged and no
            work starts until you approve the numbers here in the portal.
          </p>
        </div>
      )}

      {project.payment_status === "quote_sent" && (
        <div className="mt-10 border-2 border-[#FF2D2D] bg-[#FF2D2D]/5 p-6" data-testid="quote-approval-panel">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Quote ready — review &amp; approve</div>
              <div className="font-display text-4xl mt-2 tabular-nums">{formatUSD(project.total_usd)}</div>
              {Number(project.deposit_usd || 0) > 0 && (
                <div className="text-sm text-[#a3a3a3] mt-1">
                  Deposit due at approval: <span className="text-[#fafafa]">{formatUSD(project.deposit_usd)}</span>
                </div>
              )}
              {project.quote_note && (
                <div className="mt-4 border-l-2 border-[#FF2D2D] bg-[#0a0a0a] p-3 text-sm text-[#fafafa] whitespace-pre-wrap max-w-2xl" data-testid="quote-note">
                  {project.quote_note}
                </div>
              )}
              <p className="text-xs text-[#a3a3a3] mt-4 max-w-2xl">
                Approving locks the scope for your RFP records — you'll receive a signed-quote PDF by
                email and can download it here. Payment options unlock only after approval.
              </p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button
                type="button"
                onClick={approveQuote}
                disabled={approvingQuote}
                data-testid="approve-quote-btn"
                className="btn-primary px-6 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-50 flex items-center gap-2"
              >
                <CheckCircle size={16} weight="bold" /> {approvingQuote ? "Approving…" : "Approve quote"}
              </button>
              <div className="text-[10px] text-[#a3a3a3] text-center uppercase tracking-wider">
                One-click · legally binding
              </div>
            </div>
          </div>
        </div>
      )}

      {project.quote_approval && (
        <div className="mt-6 border border-[#22c55e]/40 bg-[#22c55e]/5 p-4 flex flex-wrap items-center gap-4" data-testid="signed-quote-banner">
          <CheckCircle size={18} weight="fill" className="text-[#22c55e]" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#22c55e]">Quote approved</div>
            <div className="text-sm text-[#fafafa]">
              Signed by {project.quote_approval.approved_by_name} on{" "}
              {new Date(project.quote_approval.at).toLocaleString()}
            </div>
          </div>
          <button
            type="button"
            onClick={downloadSignedQuote}
            data-testid="download-signed-quote"
            className="btn-outline px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2"
          >
            <FileArrowDown size={14} /> Signed quote PDF
          </button>
        </div>
      )}

      {/* Payment block */}
      {!isPaidPoints && balance > 0 && project.payment_status !== "quote_sent" && project.payment_status !== "awaiting_quote" && (
        <div className="mt-10 border border-[#FF2D2D] bg-[#FF2D2D]/5 p-6 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between" data-testid="payment-block">
          <div className="flex-1 w-full">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Balance due</div>
            <div className="font-display text-4xl mt-1">{formatUSD(balance)}</div>
            <PromoCodeField
              value={promo}
              onChange={setPromo}
              previewContext={{ type: "project_balance", reference_id: id }}
            />
            <div className="text-sm text-[#a3a3a3] mt-3">
              Deposit required: {formatUSD(project.deposit_usd)} • Paid: {formatUSD(project.paid_amount)}
            </div>
            {feePct > 0 && (
              <div className="text-[11px] text-[#FFA94D] mt-3 border border-[#FFA94D]/30 bg-[#FFA94D]/5 px-3 py-2" data-testid="project-fee-notice">
                A {Number(feePct).toFixed(1)}% processing fee is added at Stripe checkout
                (~{formatUSD(balance * feePct / 100)} on this balance). Pay by Zelle/CashApp/cash to skip it — contact us to arrange.
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            {project.paid_amount < project.deposit_usd && (
              <button
                onClick={() => startPayment("project_deposit")}
                disabled={paying}
                data-testid="pay-deposit"
                className="btn-outline px-5 py-3 text-sm uppercase tracking-wider flex items-center gap-2"
              >
                <CreditCard size={16} /> Pay deposit
              </button>
            )}
            <button
              onClick={() => startPayment("project_balance")}
              disabled={paying}
              data-testid="pay-balance"
              className="btn-primary px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2"
            >
              <CreditCard size={16} weight="bold" /> Pay {formatUSD(balance)}
            </button>
          </div>
        </div>
      )}

      {isPaidPoints && (
        <div className="mt-10 border border-[#262626] bg-[#0a0a0a] p-6 flex items-center gap-3">
          <Coins size={24} className="text-[#FF2D2D]" weight="fill" />
          <span>Paid with {project.points_used} points.</span>
        </div>
      )}

      {/* Studio price adjustments (admin discounts / credits / write-offs) */}
      {(project.adjustments || []).length > 0 && (
        <div className="mt-8" data-testid="adjustments-section">
          <h2 className="font-display text-2xl mb-4 uppercase">Studio adjustments</h2>
          <div className="border border-[#262626] divide-y divide-[#262626]">
            {(project.adjustments || []).map((adj) => (
              <div
                key={adj.id}
                data-testid={`adjustment-${adj.id}`}
                className="bg-[#0a0a0a] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">
                    {adj.action === "discount_total" && "Discount on total"}
                    {adj.action === "credit_balance" && "Studio credit"}
                    {adj.action === "mark_paid_in_full" && "Marked paid in full"}
                  </div>
                  {adj.note && (
                    <div className="text-sm text-white mt-1 truncate">{adj.note}</div>
                  )}
                  <div className="text-xs text-[#a3a3a3] mt-1">
                    {adj.created_at && new Date(adj.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="font-display text-2xl text-[#FF2D2D] tabular-nums shrink-0">
                  −{formatUSD(adj.amount_applied_usd || 0)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payments received (admin-recorded offline payments) */}
      {(project.payments || []).length > 0 && (
        <div className="mt-8" data-testid="payments-section">
          <h2 className="font-display text-2xl mb-4 uppercase">Payments received</h2>
          <div className="border border-[#262626] divide-y divide-[#262626]">
            {(project.payments || []).map((pay) => (
              <div
                key={pay.id}
                data-testid={`payment-${pay.id}`}
                className="bg-[#0a0a0a] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#28C878]">
                    {(pay.method || "other").replace("_", " ")}{pay.marked_paid_in_full ? " · Paid in full" : ""}
                  </div>
                  {pay.note && (
                    <div className="text-sm text-white mt-1 truncate">{pay.note}</div>
                  )}
                  <div className="text-xs text-[#a3a3a3] mt-1">
                    {pay.paid_at && new Date(pay.paid_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="font-display text-2xl text-[#28C878] tabular-nums shrink-0">
                  +{formatUSD(pay.amount_usd || 0)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Client-supplied references (attachments + links) */}
      {((project.deliverables || []).some((d) => d.kind === "client_attachment") || (project.reference_links || []).length > 0) && (
        <div className="mt-12" data-testid="client-references">
          <h2 className="font-display text-2xl mb-4 uppercase">Brief & references</h2>
          <div className="border border-[#262626] divide-y divide-[#262626]">
            {(project.reference_links || []).map((link, i) => (
              <a
                key={`link-${i}`}
                href={link.startsWith("http") ? link : `https://${link}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`ref-link-${i}`}
                className="bg-[#0a0a0a] p-4 flex items-center gap-3 hover:bg-[#121212] transition-colors"
              >
                <LinkSimple size={18} className="text-[#FF2D2D] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs uppercase tracking-wider text-[#a3a3a3]">Reference link</div>
                  <div className="text-sm text-white truncate">{link}</div>
                </div>
              </a>
            ))}
            {(project.deliverables || [])
              .filter((d) => d.kind === "client_attachment")
              .map((d) => (
                <div key={d.id} className="bg-[#0a0a0a] p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0 flex items-center gap-3">
                    <ImageSquare size={18} className="text-[#FF2D2D] shrink-0" />
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-wider text-[#a3a3a3]">Client upload</div>
                      <div className="font-semibold truncate">{d.original_filename}</div>
                      <div className="text-xs text-[#a3a3a3] truncate">{(d.size / 1024).toFixed(1)} KB · {d.content_type}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => downloadFile(d)}
                    data-testid={`download-attachment-${d.id}`}
                    className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2 shrink-0"
                  >
                    <Download size={12} /> Download
                  </button>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Deliverables */}
      <div className="mt-12">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 className="font-display text-2xl uppercase">Deliverables</h2>
          {project.deliverables && project.deliverables.filter((d) => d.kind !== "link" && d.kind !== "client_attachment").length > 1 && (
            <button
              onClick={downloadAllZip}
              data-testid="client-download-all-zip"
              className="btn-outline px-3 py-2 text-xs uppercase tracking-wider flex items-center gap-2"
              title="Bundle every file on this project into a single ZIP"
            >
              <Archive size={12} weight="bold" /> Download all (.zip)
            </button>
          )}
        </div>
        {!project.deliverables || project.deliverables.filter((d) => d.kind !== "client_attachment").length === 0 ? (
          <div className="border border-[#262626] bg-[#0a0a0a] p-8 text-center text-[#a3a3a3]" data-testid="no-deliverables">
            <FileArrowDown size={32} className="mx-auto mb-2 text-[#a3a3a3]" />
            Nothing to download yet. Once we finish, files will appear here.
          </div>
        ) : (
          <div className="border border-[#262626] divide-y divide-[#262626]" data-testid="deliverables-list">
            {project.deliverables.filter((d) => d.kind !== "client_attachment").map((d) => (
              <div key={d.id} className="bg-[#0a0a0a] p-4 flex items-center justify-between gap-4">
                <div className="min-w-0 flex items-center gap-3">
                  {d.kind === "link" ? <Download size={18} className="text-[#FF2D2D] shrink-0 rotate-[-45deg]" /> : null}
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{d.original_filename}</div>
                    <div className="text-xs text-[#a3a3a3] truncate">
                      {d.kind === "link" ? d.url : `${(d.size / 1024).toFixed(1)} KB · ${d.content_type}`}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => downloadFile(d)}
                  data-testid={`download-${d.id}`}
                  className="btn-primary px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 shrink-0"
                >
                  {d.kind === "link" ? "Open →" : <><Download size={14} weight="bold" /> Download</>}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* History */}
      <div className="mt-12">
        <h2 className="font-display text-2xl mb-4 uppercase">Activity</h2>
        <div className="space-y-3">
          {(project.status_history || []).map((h, i) => (
            <div key={i} className="border-l-2 border-[#FF2D2D] pl-4 py-1">
              <div className="text-xs text-[#a3a3a3]">{new Date(h.at).toLocaleString()}</div>
              <div className="text-sm font-semibold capitalize">{h.status}</div>
              {h.note && <div className="text-sm text-[#a3a3a3]">{h.note}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Exit survey (clients only, after delivery) */}
      {user?.role === "client" && project.status === "delivered" && !project.client_approved && (
        <div className="mt-12 border border-[#FF2D2D] bg-gradient-to-b from-[#FF2D2D]/10 to-transparent p-6" data-testid="approve-block">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Final step</div>
          <h2 className="font-display text-3xl mt-2">All looks good?</h2>
          <p className="text-sm text-[#a3a3a3] mt-3 leading-relaxed">
            When you're happy with the deliverables, hit approve to close out the project. It'll move to your archive (you can still view it anytime). After that, we'll invite you to leave a quick review.
          </p>
          <div className="flex gap-3 mt-5 flex-wrap">
            <button
              onClick={approveAndClose}
              disabled={approving}
              data-testid="approve-project-btn"
              className="btn-primary px-6 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
            >
              <CheckCircle size={16} weight="fill" />
              {approving ? "Closing…" : "Approve & close"}
            </button>
            <button
              onClick={() => setReviewModal(true)}
              data-testid="leave-review-btn"
              className="btn-outline px-5 py-3 text-sm uppercase tracking-wider flex items-center gap-2"
            >
              <Star size={14} /> Leave a review
            </button>
          </div>
        </div>
      )}

      {/* Launch image generator — web projects only, once delivered */}
      {project.service_type === "web" && project.status === "delivered" && (
        <div className="mt-8 border border-[#262626] bg-[#0a0a0a] p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="launch-image-block-client">
          <div className="flex items-center gap-3 min-w-0">
            <ImageSquare size={24} className="text-[#FF2D2D] shrink-0" weight="fill" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Launch image</div>
              <div className="text-sm">Generate a "Vision Brought to Life" share graphic of your new site.</div>
              <div className="text-[10px] text-[#a3a3a3] mt-0.5">Includes laptop + phone mockup variants and pre-filled captions.</div>
            </div>
          </div>
          <button
            onClick={() => setLaunchOpen(true)}
            data-testid="open-launch-image-client"
            className="btn-outline px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2 shrink-0"
          >
            Generate →
          </button>
        </div>
      )}

      {/* Archived banner (project is closed) */}
      {project.archived && (
        <div className="mt-12 border border-[#262626] bg-[#0a0a0a] p-5 flex items-center gap-3" data-testid="archived-banner">
          <Archive size={18} className="text-[#a3a3a3]" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3]">Archived</div>
            <div className="text-sm">This project was {project.client_approved ? "approved by you" : "archived by the studio"} on {new Date(project.archived_at).toLocaleDateString()}.</div>
          </div>
          {!project.client_approved && (
            <button onClick={() => setReviewModal(true)} className="btn-outline px-4 py-2 text-xs uppercase tracking-wider">Leave a review</button>
          )}
        </div>
      )}

      {/* Exit survey */}
      {user?.role === "client" && project.status === "delivered" && (
        <div className="mt-8">
          <ExitSurvey projectId={id} designerName={project.assigned_designer_name} />
        </div>
      )}

      {/* Messages */}
      <div className="mt-12">
        <h2 className="font-display text-2xl mb-4 uppercase">Messages</h2>
        <ProjectMessages projectId={id} />
      </div>

      <ReviewSubmitModal
        open={reviewModal}
        project={project}
        onClose={() => setReviewModal(false)}
        onSubmitted={() => {
          setReviewModal(false);
          if (project.archived) navigate("/dashboard");
          else load();
        }}
      />

      <LaunchImageModal
        open={launchOpen}
        projectId={project?.id}
        isAdmin={user?.role === "admin"}
        onClose={() => setLaunchOpen(false)}
      />
    </ClientLayout>
  );
}
